/**
 * Hidden iframe token refresh service for seamless authentication
 * This service manages iframe creation, communication, and token refresh
 * without exposing any identity logic to the main application
 */

interface RefreshResponse {
  success: boolean;
  access_token?: string;
  refresh_token?: string;
  error?: string;
}

/**
 * Minimal shape of a request that can be queued and retried after refresh.
 * Only `headers` is read/written here (to inject the new Bearer token);
 * any other request-specific fields are preserved opaquely.
 */
interface QueuedRequestPayload {
  headers?: Record<string, string>;
  [key: string]: unknown;
}

interface QueuedRequest {
  resolve: (value: QueuedRequestPayload) => void;
  reject: (error: unknown) => void;
  originalRequest: QueuedRequestPayload;
}

class HiddenIframeRefreshService {
  private iframe: HTMLIFrameElement | null = null;
  private isRefreshing = false;
  private requestQueue: QueuedRequest[] = [];
  private refreshPromise: Promise<RefreshResponse> | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  
  private readonly IFRAME_TIMEOUT = 30000; // 30 seconds
  private readonly IFRAME_ID = 'darwin-auth-refresh-iframe';

  constructor(
    private authDomain: string,
    private darwinLoginUrl: string
  ) {}

  /**
   * Attempt to refresh tokens using hidden iframe
   */
  async refreshTokens(): Promise<RefreshResponse> {
    console.log('🔄 Hidden iframe refresh: Starting token refresh');
    
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      console.log('🔄 Hidden iframe refresh: Already refreshing, returning existing promise');
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const result = await this.refreshPromise;
      console.log('✅ Hidden iframe refresh: Token refresh completed successfully');
      return result;
    } catch (error) {
      console.error('❌ Hidden iframe refresh: Token refresh failed:', error);
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Queue a request to be retried after token refresh
   */
  queueRequest(originalRequest: QueuedRequestPayload): Promise<QueuedRequestPayload> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        resolve,
        reject,
        originalRequest
      });
    });
  }

  /**
   * Process all queued requests with new token
   */
  private async processQueuedRequests(newToken?: string): Promise<void> {
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    for (const { resolve, reject, originalRequest } of queue) {
      try {
        if (newToken) {
          // Update the request with new token
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        // Re-execute the request (this would be handled by the calling code)
        resolve(originalRequest);
      } catch (error) {
        reject(error);
      }
    }
  }

  /**
   * Perform the actual token refresh using hidden iframe
   */
  private async performRefresh(): Promise<RefreshResponse> {
    return new Promise((resolve, reject) => {
      try {
        console.log('🔄 Hidden iframe refresh: Creating iframe and setting up listeners');
        
        this.createIframe();
        this.setupMessageListener(resolve, reject);
        this.startRefreshTimeout(reject);
        
        // Navigate iframe to Darwin govern using fresh iframe context
        // Complete iframe recreation forces Darwin to generate fresh tokens
        if (this.iframe) {
          console.log('🔄 Hidden iframe refresh: Navigating fresh iframe to Darwin govern:', this.darwinLoginUrl);
          console.log('🔄 Fresh iframe context should force Darwin to generate new tokens instead of cached expired ones');
          this.iframe.src = this.darwinLoginUrl;
        } else {
          console.error('❌ Hidden iframe refresh: Failed to create iframe');
          reject({ success: false, error: 'Failed to create iframe' });
        }
      } catch (error) {
        console.error('❌ Hidden iframe refresh: Error in performRefresh:', error);
        this.cleanup();
        reject({ success: false, error: (error as Error).message });
      }
    });
  }

  /**
   * Create hidden iframe for token refresh
   * Always completely recreates iframe to force fresh browser context
   */
  private createIframe(): void {
    console.log('🔄 Hidden iframe refresh: Creating completely fresh iframe to force new browser context');
    
    // Always remove existing iframe completely to ensure fresh context
    this.cleanup();

    // Create brand new iframe element
    this.iframe = document.createElement('iframe');
    this.iframe.id = this.IFRAME_ID;
    this.iframe.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 1px;
      height: 1px;
      opacity: 0;
      border: none;
      pointer-events: none;
    `;
    
    // Set iframe attributes for security
    this.iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms');
    this.iframe.setAttribute('referrerpolicy', 'no-referrer');
    
    // Add fresh iframe to DOM
    document.body.appendChild(this.iframe);
    console.log('✅ Hidden iframe refresh: Fresh iframe created and added to DOM');
  }

  /**
   * Setup message listener for iframe communication
   */
  private setupMessageListener(
    resolve: (value: RefreshResponse) => void,
    reject: (error: RefreshResponse) => void
  ): void {
    this.messageHandler = (event: MessageEvent) => {
      console.log('🔄 Hidden iframe refresh: Received message from origin:', event.origin, 'data:', event.data);
      
      // Verify origin for security
      const allowedOrigins = [
        this.authDomain,
        new URL(this.darwinLoginUrl).origin,
        window.location.origin
      ];

      console.log('🔄 Hidden iframe refresh: Allowed origins:', allowedOrigins);

      if (!allowedOrigins.includes(event.origin)) {
        console.warn('❌ Hidden iframe refresh: Received message from unauthorized origin:', event.origin);
        return;
      }

      const data = event.data;
      
      // Handle different message types
      if (data.type === 'DARWIN_AUTH_REFRESH_SUCCESS') {
        console.log('✅ Hidden iframe refresh: Received success message');
        console.log('🔍 Token comparison - access_token preview:', data.access_token?.substring?.(0, 30) + '...');
        this.cleanup();
        this.processQueuedRequests(data.access_token);
        resolve({
          success: true,
          access_token: data.access_token,
          refresh_token: data.refresh_token
        });
      } else if (data.type === 'DARWIN_AUTH_REFRESH_ERROR') {
        console.log('❌ Hidden iframe refresh: Received error message:', data.error);
        this.cleanup();
        this.processQueuedRequests(); // Process queue without new token (will likely fail)
        reject({
          success: false,
          error: data.error || 'Token refresh failed'
        });
      } else if (data.type === 'DARWIN_AUTH_REFRESH_TIMEOUT') {
        console.log('⏰ Hidden iframe refresh: Received timeout message');
        this.cleanup();
        this.processQueuedRequests();
        reject({
          success: false,
          error: 'Token refresh timed out'
        });
      } else if (data.access_token && data.refresh_token) {
        // Handle Darwin's actual message format (without explicit type)
        console.log('✅ Hidden iframe refresh: Received Darwin token message (success)');
        console.log('🔍 Full Darwin response:', JSON.stringify(data, null, 2));
        
        const accessToken = data.access_token.token || data.access_token;
        const refreshToken = data.refresh_token.token || data.refresh_token;
        
        console.log('🔍 Token details:');
        console.log('  - Access token preview:', accessToken.substring(0, 30) + '...');
        console.log('  - Access token exp:', data.access_token.exp);
        console.log('  - Refresh token preview:', refreshToken.substring(0, 30) + '...');
        console.log('  - Refresh token exp:', data.refresh_token.exp);
        
        this.cleanup();
        this.processQueuedRequests(accessToken);
        resolve({
          success: true,
          access_token: accessToken,
          refresh_token: refreshToken
        });
      } else {
        console.log('🔄 Hidden iframe refresh: Received unknown message type:', data.type, 'Full data:', data);
      }
    };

    window.addEventListener('message', this.messageHandler);
  }

  /**
   * Start timeout for iframe refresh operation
   */
  private startRefreshTimeout(reject: (error: RefreshResponse) => void): void {
    this.timeoutId = setTimeout(() => {
      console.warn('Hidden iframe refresh timed out');
      this.cleanup();
      this.processQueuedRequests();
      reject({
        success: false,
        error: 'Token refresh operation timed out'
      });
    }, this.IFRAME_TIMEOUT);
  }

  /**
   * Clean up iframe and listeners
   * Ensures complete removal for fresh iframe creation
   */
  private cleanup(): void {
    // Remove message listener
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    // Clear timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Remove iframe completely from DOM
    if (this.iframe) {
      try {
        // Set src to about:blank first to clear any loaded content
        this.iframe.src = 'about:blank';
        document.body.removeChild(this.iframe);
        console.log('🧹 Hidden iframe refresh: Previous iframe completely removed from DOM');
      } catch (error) {
        // Iframe might already be removed
        console.log('🧹 Hidden iframe refresh: Iframe already removed or error removing:', error);
      }
      this.iframe = null;
    }
  }

  /**
   * Check if currently refreshing tokens
   */
  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  /**
   * Destroy the service and clean up all resources
   */
  destroy(): void {
    this.cleanup();
    this.requestQueue = [];
    this.isRefreshing = false;
    this.refreshPromise = null;
  }
}

// Singleton instance
let hiddenIframeRefreshService: HiddenIframeRefreshService | null = null;

/**
 * Get or create the hidden iframe refresh service instance
 */
export const getHiddenIframeRefreshService = (
  authDomain?: string,
  darwinLoginUrl?: string
): HiddenIframeRefreshService => {
  if (!hiddenIframeRefreshService && authDomain && darwinLoginUrl) {
    hiddenIframeRefreshService = new HiddenIframeRefreshService(authDomain, darwinLoginUrl);
  }

  if (!hiddenIframeRefreshService) {
    throw new Error('Hidden iframe refresh service not initialized. Call with authDomain and darwinLoginUrl first.');
  }

  return hiddenIframeRefreshService;
};

/**
 * Initialize the hidden iframe refresh service
 */
export const initializeHiddenIframeRefresh = (
  authDomain: string,
  darwinLoginUrl: string
): HiddenIframeRefreshService => {
  if (hiddenIframeRefreshService) {
    hiddenIframeRefreshService.destroy();
  }
  
  hiddenIframeRefreshService = new HiddenIframeRefreshService(authDomain, darwinLoginUrl);
  return hiddenIframeRefreshService;
};

export type { RefreshResponse };